import React from 'react';
import { Check } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

/**
 * The one journey from "I want Ruta" to "I'm in", as the person sees it. Signup
 * (`pages/company/Onboarding.tsx`) owns 1–2 (your account, then you and your
 * company; the emailed code is the second screen of step 1), the setup wizard
 * (`OptimizedOnboarding.tsx`) owns 3, and the emailed link
 * (`pages/auth/ConfirmEmail.tsx`) lands on step 1's code screen — all inside
 * this same shell, so it reads as one thing with one list of steps (RUT-4518).
 *
 * Three steps, then Live (fast signup). Getting paid and bringing customers
 * over are no longer signup steps: they wait inside Ruta until the owner
 * needs what they unlock.
 */
export const SETUP_JOURNEY = [
  { n: 1, title: 'Create your account', hint: 'Google, Apple or email' },
  { n: 2, title: 'You and your company', hint: 'Name, company, where you work' },
  { n: 3, title: 'Start your free trial', hint: '30 days, no contracts' },
] as const;

export type JourneyStep = (typeof SETUP_JOURNEY)[number]['n'];
export const JOURNEY_LENGTH = SETUP_JOURNEY.length;

/** "Step n of 3" — the eyebrow every page of the journey wears. */
export function journeyEyebrow(step: JourneyStep): string {
  return `Step ${step} of ${JOURNEY_LENGTH}`;
}

interface SetupShellProps {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  /** A shorter lead for a phone (below `lg`), where the band has room for one line. Desktop keeps `lead`. */
  leadShort?: React.ReactNode;
  /** Where in the journey this page is. Earlier steps show as done; omit to hide the list. */
  current?: JourneyStep | 'done';
  /** Pricing and the import need the whole width; a form does not. */
  wide?: boolean;
  /** Bottom of the dark side — a sign-out link, a reassurance. */
  asideFooter?: React.ReactNode;
  children: React.ReactNode;
}

/** Below `lg` the page is a phone: compact band on top, the action row pinned to the bottom. */
const PHONE_QUERY = '(max-width: 1023.98px)';

/**
 * True inside a setup page. StepActions is also used by the data import in Settings,
 * which must keep its plain row on a phone, so only the setup page pins it.
 */
const InSetupShell = React.createContext(false);

/**
 * The page every step of setup is drawn on: the dark side says where they are and
 * what this page is for, the light side asks its few questions, big enough to read.
 *
 * On a phone (below `lg`, RUT-4448) the dark side folds into a band about a quarter
 * of the screen tall: the wordmark and "Step n of 3" on one row, a three-part bar,
 * the title and a one-line lead. The same elements draw both layouts (the band is a
 * grid, the side a column), so the page has one heading and desktop is unchanged.
 */
export function SetupShell({ eyebrow, title, lead, leadShort, current, wide = false, asideFooter, children }: SetupShellProps) {
  const position = current === 'done' ? JOURNEY_LENGTH + 1 : current ?? 0;

  // On a touch phone the keyboard takes half the screen: once it is up, bring the
  // field being typed clear of the pinned action row (the inputs' scroll-margin
  // keeps them above it). A suggestion list (the address) goes to the top so its
  // options have room. Anyone who scrolls in the meantime keeps their scroll.
  const onFocusField = React.useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia(PHONE_QUERY).matches || !window.matchMedia('(pointer: coarse)').matches) return;
    let userScrolled = false;
    const onTouch = () => {
      userScrolled = true;
    };
    window.addEventListener('touchmove', onTouch, { once: true, passive: true });
    window.setTimeout(() => {
      window.removeEventListener('touchmove', onTouch);
      if (userScrolled || document.activeElement !== field) return;
      const suggests = field.getAttribute('role') === 'combobox' || field.hasAttribute('aria-autocomplete') || field.id === 'address';
      field.scrollIntoView({ block: suggests ? 'start' : 'nearest', behavior: 'smooth' });
    }, 350);
  }, []);

  return (
    <InSetupShell.Provider value={true}>
    <div
      className={cn(
        'flex min-h-[100dvh] flex-col bg-background lg:grid lg:min-h-screen',
        wide
          ? 'lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] xl:grid-cols-[minmax(0,3fr)_minmax(0,9fr)]'
          : 'lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]',
      )}
    >
      <aside className="relative grid shrink-0 grid-cols-[1fr_auto] flex-col justify-between overflow-hidden bg-nav px-5 py-[16px] text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:px-12 lg:py-12">
        <Glow />
        <div className="relative z-10 self-center lg:self-auto">
          <p className="font-black text-lg leading-6 tracking-wider lg:text-2xl">RUTA</p>
        </div>

        {/* The headline repeats the company name and the email: masked in replays (RUT-4433).
            On a phone this box steps aside (display: contents) so its lines sit in the band's grid. */}
        <div className="contents lg:relative lg:z-10 lg:my-0 lg:block" data-replay-mask="">
          <p className="relative z-10 col-start-2 row-start-1 self-center text-xs font-medium uppercase tracking-[0.2em] text-white/50 lg:static lg:mb-3">{eyebrow}</p>
          {current !== undefined && (
            <div className="relative z-10 col-span-2 mb-3.5 mt-3 flex gap-1 lg:hidden" aria-hidden data-testid="journey-progress">
              {SETUP_JOURNEY.map((step) => (
                <span
                  key={step.n}
                  className={cn('h-1 flex-1 rounded-full', position >= step.n ? 'bg-white' : 'bg-white/[0.18]')}
                />
              ))}
            </div>
          )}
          <h1 className="relative z-10 col-span-2 text-2xl font-bold leading-[1.15] tracking-tight lg:static lg:text-[2.6rem] lg:leading-[1.1]">{title}</h1>
          {lead && (
            <p className="relative z-10 col-span-2 mt-1.5 max-w-md text-[15px] leading-[1.4] text-white/[0.72] lg:static lg:mt-4 lg:text-lg lg:text-white/70">
              {leadShort && leadShort !== lead ? (
                <>
                  <span className="lg:hidden">{leadShort}</span>
                  <span className="hidden lg:inline">{lead}</span>
                </>
              ) : (
                lead
              )}
            </p>
          )}

          {current !== undefined && (
            <ol className="mt-10 hidden space-y-3.5 lg:block" aria-label="Setup steps">
              {SETUP_JOURNEY.map((step) => {
                const done = position > step.n;
                const isCurrent = position === step.n;
                return (
                  <li key={step.n} className="flex items-center gap-4">
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
                        done && 'border-white bg-white text-nav',
                        isCurrent && 'border-white bg-white/10 text-white',
                        !done && !isCurrent && 'border-white/25 text-white/40',
                      )}
                    >
                      {done ? <Check className="h-4 w-4" weight="bold" /> : step.n}
                    </span>
                    <span>
                      <span className={cn('block text-[15px] font-medium leading-tight', isCurrent || done ? 'text-white' : 'text-white/40')}>
                        {step.title}
                      </span>
                      <span className={cn('block text-sm leading-tight', isCurrent ? 'text-white/60' : 'text-white/30')}>
                        {step.hint}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="relative z-10 hidden lg:block">{asideFooter}</div>
      </aside>

      {/* Centred on the page. Content taller than the window simply starts at the top —
          the column is as tall as its content, so centring never hides anything. */}
      <main
        className={cn(
          'flex flex-1 items-stretch justify-center px-5 pt-[18px] lg:items-center lg:py-12',
          wide ? 'lg:px-10 xl:px-14' : 'lg:px-16',
        )}
      >
        <div
          className={cn(
            'w-full',
            wide ? 'max-w-6xl' : 'max-w-2xl',
            // Phone: a column the step body fills, so the action row rests at the bottom
            // of a short page; inputs stop clear of that row when scrolled to.
            'max-lg:flex max-lg:flex-col max-lg:[&_input]:scroll-mb-40',
          )}
          data-replay-mask=""
          data-setup-column=""
          onFocus={onFocusField}
        >
          {children}
        </div>
      </main>
    </div>
    </InSetupShell.Provider>
  );
}

/** The soft green light behind the dark side — the same glow the login page has. */
export function Glow() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.15) 0%, transparent 50%)' }} />
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)' }} />
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)' }} />
    </div>
  );
}

/**
 * The one action row every step ends with: what steps back or aside on the LEFT, the one
 * action that moves forward on the RIGHT, always in the same two places. A step with no
 * primary yet (a form the partner owns, a drop zone with its own button) leaves the right empty.
 *
 * On a phone (RUT-4448) the row is pinned to the bottom of the screen over a fade of the
 * page: the primary is full width and 56px tall, Back sits to its left, a link that is not
 * a step back ("Already have an account? Log in") goes centred under it (`secondaryBelow`),
 * and a skip with nothing beside it is a full-width button with its note above.
 */
export function StepActions({
  secondary,
  primary,
  note,
  secondaryBelow = false,
  className,
}: {
  secondary?: React.ReactNode;
  primary?: React.ReactNode;
  note?: React.ReactNode;
  /** Phone only: put `secondary` centred under the primary instead of beside it. */
  secondaryBelow?: boolean;
  className?: string;
}) {
  const pinned = React.useContext(InSetupShell);
  if (!pinned) {
    // Outside a setup page (Settings → Data import): the plain row, as before.
    return (
      <div className={cn('mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6', className)}>
        <div className="flex items-center gap-3">{secondary}</div>
        <div className="flex items-center gap-4">
          {note && <div className="text-xs text-stone-500">{note}</div>}
          {primary}
        </div>
      </div>
    );
  }

  const skipOnly = !primary && !!secondary;
  const stacked = skipOnly || (secondaryBelow && !!secondary);
  return (
    <>
    {/* Phone: on a short page this takes up the slack so the row rests at the bottom; on a
        long one it is empty and the row keeps its usual gap. Nothing on desktop. */}
    <div aria-hidden className="hidden max-lg:block max-lg:flex-1" data-step-actions-spacer="" />
    <div
      data-step-actions=""
      className={cn(
        'mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6',
        // Phone: pinned to the bottom on the page colour, which fades out above it so the
        // page scrolls away under the row instead of being cut at a hard edge.
        'max-lg:sticky max-lg:bottom-0 max-lg:z-20 max-lg:-mx-5 max-lg:flex-nowrap max-lg:gap-2.5 max-lg:border-t-0 max-lg:bg-background max-lg:px-5 max-lg:pb-[calc(16px+env(safe-area-inset-bottom))] max-lg:pt-2.5',
        'max-lg:before:pointer-events-none max-lg:before:absolute max-lg:before:inset-x-0 max-lg:before:bottom-full max-lg:before:h-3 max-lg:before:bg-gradient-to-t max-lg:before:from-background max-lg:before:to-background/0 max-lg:before:content-[""]',
        stacked && 'max-lg:flex-col max-lg:items-stretch max-lg:gap-1.5',
        !primary && !secondary && !note && 'max-lg:hidden',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3',
          'max-lg:shrink-0 max-lg:empty:hidden max-lg:[&>button]:h-14',
          skipOnly && 'max-lg:[&>button]:w-full',
          secondaryBelow && 'max-lg:order-last max-lg:justify-center max-lg:text-center',
        )}
      >
        {secondary}
      </div>
      <div
        className={cn(
          'flex items-center gap-4',
          'max-lg:min-w-0 max-lg:flex-1 max-lg:empty:hidden max-lg:[&>button]:h-14 max-lg:[&>button]:w-full max-lg:[&>button]:text-[17px] max-lg:[&>button]:font-semibold',
          skipOnly && 'max-lg:order-first max-lg:justify-center',
        )}
      >
        {note && <div className="text-xs text-stone-500 max-lg:mb-1 max-lg:text-center max-lg:text-[13px]">{note}</div>}
        {primary}
      </div>
    </div>
    </>
  );
}

/**
 * "Where you work" (fast signup): a city or ZIP, stored as the company's
 * `serviceArea`. The geocoder turns it into the point the map opens on. It is
 * deliberately not the mailing address: payouts and texting registration need
 * a full street address and ask for it when they are set up.
 */
export const SERVICE_AREA_FIELD = {
  label: 'Where you work',
  placeholder: 'City or ZIP, e.g. Minneapolis, MN',
  hint: 'Centers your map on your area.',
} as const;

/** Same field sizing on every page of the journey, so a form never shrinks between steps. */
export const journeyField = 'h-14 text-lg px-4';
/** Phone: the label sits 4px over its field; desktop keeps 8px. */
export const journeyLabel = 'mb-1 block text-base font-medium text-stone-700 lg:mb-2';
/** Space between fields: 14px on a phone, 32px on desktop. */
export const journeyFieldGap = 'space-y-3.5 lg:space-y-8';
/** A step's form or body: on a phone it fills the column so its action row can rest at the bottom. */
export const journeyStepBody = 'max-lg:flex max-lg:flex-1 max-lg:flex-col';
/** A hint or error under a field: 4px under it on a phone, 8px on desktop. */
export const journeyHintGap = 'mt-1 lg:mt-2';
